import adaptiveApi from 'api/adaptiveAiApi';
import Loading from 'components/Layout/Loading';
import ThemeButton from 'components/AntdFormInputs/theme-button';
import DataTable from 'components/React-table/data-table';
import { handleToastError } from 'helpers';
import useFetch from 'hooks/use-fetch';
import React, { useEffect, useMemo } from 'react';
import { Eye } from 'react-feather';

/**
 * @param {any} data - API payload (array or wrapped)
 * @returns {any[]}
 */
function normalizeAttemptRows(data) {
  if (data == null) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.results)) return data.results;
  return [];
}

/**
 * @param {any} record
 */
function getRowUser(record) {
  if (record?.user && typeof record.user === 'object') {
    return record.user;
  }
  return {
    firstName: record?.firstName,
    lastName: record?.lastName,
    fullName: record?.fullName,
    email: record?.email,
  };
}

/**
 * @param {any} user
 */
function formatUserDisplayName(user) {
  if (!user) return '—';
  if (user.fullName && String(user.fullName).trim()) return user.fullName;
  const parts = [user.firstName, user.lastName].filter(Boolean);
  if (parts.length) return parts.join(' ');
  if (user.email) return user.email;
  return '—';
}

/**
 * DataTable uses getRowId: (row) => row._id — ensure every row has _id.
 * @param {any[]} rows
 */
function withStableRowIds(rows) {
  return rows.map((record, index) => {
    const user = getRowUser(record);
    const id = user?._id ?? user?.id ?? user?.email ?? `attempt-row-${index}`;
    return { ...record, _id: String(id) };
  });
}

const AttemptTable = ({ unitId }) => {
  const { data, loading, error } = useFetch({
    queryKey: ['attempt-table', unitId],
    queryFn: () => adaptiveApi.getContentHistory({ unitId }),
    enable: !!unitId,
  });

  useEffect(() => {
    if (error) {
      handleToastError(error);
    }
  }, [error]);

  const rows = useMemo(() => normalizeAttemptRows(data), [data]);
  const tableData = useMemo(() => withStableRowIds(rows), [rows]);

  const columns = useMemo(
    () => [
      {
        header: 'Users',
        id: 'users',
        cell: ({ row }) => {
          const user = getRowUser(row.original);
          return (
            <div>
              <div className="font-weight-medium">
                {formatUserDisplayName(user)}
              </div>
              {user?.email ? (
                <div className="text-muted small">{user.email}</div>
              ) : null}
            </div>
          );
        },
      },
      {
        header: 'Attempt count',
        id: 'attemptCount',
        cell: ({ row }) => {
          const n = Array.isArray(row.original?.attempts)
            ? row.original.attempts.length
            : 0;
          return n;
        },
      },
      {
        header: 'Actions',
        id: 'actions',
        cell: () => (
          <Eye size={16} className="cursor-pointer" onClick={() => {}} />
        ),
      },
    ],
    [],
  );

  if (loading) return <Loading />;

  return (
    <div className="attempt-table w-100 mt-3">
      <DataTable
        isResize={false}
        columns={columns}
        data={tableData}
        noData={tableData.length ? undefined : 'NODATA'}
      />
    </div>
  );
};

export default AttemptTable;
